import { ICellRendererParams } from "ag-grid-community";
import { ArrowUp } from "lucide-react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  IgAnalyticsTooltip,
  IgRadio,
  IgTypography,
} from "@intelligems/ig-theme";
import { VariationFe } from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { DashboardPageName } from "@src/api/types/dashboards";
import { calculateEstImpactTb } from "@src/features/analytics/baseDashboard/utils";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { UpliftChip } from "@src/features/analytics/tables/analyticsSegmentTable/UpliftChip";
import {
  getMetricCategoryName,
  getStandardMetricByAnalyticsMetricId,
  getTabName,
  metricsConfig,
} from "@src/features/analytics/tables/analyticsSegmentTable/utils";
import { DataType, GraphConfig } from "@src/features/analytics/types";
import { useDispatch } from "@src/store";
import { updateDashboardFilter } from "@src/store/slices/dashboardFilters";
import {
  mpAnalyticsMetricDetailClick,
  mpClickSubpageEntryPoint,
} from "@src/utils/mixpanel";

export const MetricCellRenderer = (
  params: ICellRendererParams & {
    config: GraphConfig;
    controlVariationId: string;
    tableData: any[];
    storeCurrencyConfig: { locale: string; code: string };
    testDuration: number;
    pageName: DashboardPageName;
    variations: VariationFe[];
    experienceId: string;
    isCustomMetric: boolean;
  }
) => {
  const graphValueFormatter = useGraphValueFormatter();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const {
    data,
    tableData,
    config,
    storeCurrencyConfig,
    testDuration,
    controlVariationId,
    column,
    pageName,
    variations,
    experienceId,
    isCustomMetric,
  } = params;

  if (!data || !column) {
    return (
      <IgTypography variant={"body2"} color={"tertiary"}>
        ---
      </IgTypography>
    );
  }

  const rawValue = params.value?.value;
  const upliftValue = params.value?.uplift?.value;

  const calculatedImpact =
    config.hasImpactCalculation &&
    (!isCustomMetric || data?.variation_id !== controlVariationId)
      ? (() => {
          const metricKey = config?.impactMetric || config.metrics[0];
          const audience = data.audience;

          const audienceRows = tableData.filter(
            (td) => td.audience === audience
          );

          const controlRow = audienceRows.find(
            (td) => td.variation_id === controlVariationId
          );
          const variantRow = audienceRows.find(
            (td) => td.variation_id === data.variation_id
          );

          const controlValue = controlRow?.[metricKey]?.value ?? 0;
          const variantValue = variantRow?.[metricKey]?.value ?? 0;

          const audienceVisitors = audienceRows.reduce(
            (sum, td) => sum + (td?.n_visitors?.value ?? 0),
            0
          );

          return calculateEstImpactTb(
            config,
            audienceVisitors,
            controlValue,
            variantValue,
            storeCurrencyConfig,
            testDuration,
            pageName
          );
        })()
      : undefined;

  const showUplift = upliftValue !== undefined;

  const shouldShowThreeDecimals =
    Math.min(
      ...tableData.map(
        (d) => d[config.impactMetric ?? config.metrics[0]]?.value
      )
    ) < 0.09;

  const onAddKeyMetricClick = () => {
    // TODO
  };

  const onExploreMetricClick = () => {
    dispatch(
      updateDashboardFilter({
        key: "pageName",
        value: getTabName(config.metrics[0]),
      })
    );
  };

  const metricToUse = config.impactMetric ?? config.metrics[0];
  const numerator = getStandardMetricByAnalyticsMetricId(
    metricsConfig[metricToUse]?.numerator
  );
  const denominator = getStandardMetricByAnalyticsMetricId(
    metricsConfig[metricToUse]?.denominator
  );

  return (
    <Grid
      container
      direction="row"
      wrap="nowrap"
      alignItems="center"
      sx={{ justifyContent: "flex-end" }}
    >
      <IgAnalyticsTooltip
        placement={"right"}
        sx={{ color: "white !important", minWidth: "275px" }}
        title={
          data?.variation_id === controlVariationId && !isCustomMetric ? (
            ""
          ) : (
            <Grid container direction={"column"} spacing={1}>
              <Grid item>
                <IgTypography
                  sx={{
                    fontWeight: 600,
                  }}
                  color={"dark"}
                >
                  {
                    variations.find(
                      (variation) => variation.id === data.variation_id
                    )?.name
                  }
                </IgTypography>
              </Grid>

              {data?.n_visitors?.value && isCustomMetric ? (
                <Grid container item direction={"row"} alignItems={"center"}>
                  <Grid item xs={6}>
                    <IgTypography color={"dark"}>
                      # Visitors Triggered
                    </IgTypography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "left" }}>
                    <IgTypography
                      sx={{
                        fontWeight: 600,
                      }}
                      color={"dark"}
                    >
                      {isCustomMetric
                        ? data[`n_visitors_${config.metrics[0]}`]
                        : data.n_visitors.value}
                    </IgTypography>
                  </Grid>
                </Grid>
              ) : null}

              {numerator && denominator ? (
                <Grid container item>
                  <Grid
                    container
                    item
                    direction="row"
                    alignItems="center"
                    wrap="nowrap"
                  >
                    <Grid item xs={6}>
                      <IgTypography color={"dark"}>
                        {numerator?.name}
                      </IgTypography>
                    </Grid>
                    <Grid
                      container
                      item
                      xs={5}
                      alignItems="center"
                      sx={{ justifyContent: "flex-start" }}
                    >
                      <IgTypography
                        color={"dark"}
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {graphValueFormatter({
                          isAxisTick: false,
                          value: data[numerator.analyticsMetricId]?.value,
                          configDataTypes: [
                            numerator.analyticsDataType,
                          ] as DataType[],
                          decimalAmountShown: 2,
                        })}
                      </IgTypography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    item
                    direction="row"
                    alignItems="center"
                    wrap="nowrap"
                  >
                    <Grid item xs={6}>
                      <IgTypography color={"dark"}>
                        {denominator?.name}
                      </IgTypography>
                    </Grid>
                    <Grid
                      container
                      item
                      xs={5}
                      alignItems="center"
                      sx={{ justifyContent: "flex-start" }}
                    >
                      <IgTypography
                        sx={{
                          fontWeight: 600,
                        }}
                        color={"dark"}
                      >
                        {graphValueFormatter({
                          isAxisTick: false,
                          value: data[denominator.analyticsMetricId]?.value,
                          configDataTypes: [
                            denominator.analyticsDataType,
                          ] as DataType[],
                          decimalAmountShown: 2,
                        })}
                      </IgTypography>
                    </Grid>
                  </Grid>
                </Grid>
              ) : upliftValue ? (
                <Grid
                  container
                  item
                  direction="row"
                  alignItems="center"
                  wrap="nowrap"
                >
                  <Grid item xs={6}>
                    <IgTypography color={"dark"}>
                      Uplift vs Control
                    </IgTypography>
                  </Grid>
                  <Grid
                    container
                    item
                    xs={5}
                    alignItems="center"
                    sx={{ justifyContent: "flex-start" }}
                  >
                    {Math.round(upliftValue * 100) === 0 ? null : (
                      <ArrowUp
                        size={16}
                        color={
                          config.isInverted
                            ? upliftValue > 0
                              ? theme.igIconColors.error
                              : theme.igIconColors.success
                            : upliftValue > 0
                              ? theme.igIconColors.success
                              : theme.igIconColors.error
                        }
                        style={{
                          marginRight: "3px",
                          transform:
                            upliftValue < 0 ? "rotate(180deg)" : "none",
                        }}
                      />
                    )}
                    <IgTypography
                      color={"dark"}
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {Math.round(upliftValue * 100) === 0
                        ? "No Change"
                        : Math.abs(upliftValue * 100).toFixed(1)}
                      {Math.round(upliftValue * 100) === 0 ? "" : "%"}
                    </IgTypography>
                  </Grid>
                </Grid>
              ) : null}

              {calculatedImpact && (
                <Grid container item direction={"row"} alignItems={"center"}>
                  <Grid item xs={6}>
                    <IgTypography color={"dark"}>
                      Impact vs Control
                    </IgTypography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "left" }}>
                    <IgTypography
                      color={"dark"}
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {calculatedImpact}
                    </IgTypography>
                  </Grid>
                </Grid>
              )}

              <Grid
                item
                container
                direction={"row"}
                justifyContent={"space-between"}
                mt={1}
                spacing={0}
              >
                <Grid item xs={6}>
                  <IgTypography
                    onClick={() => {
                      if (pageName === "overview") {
                        onExploreMetricClick();
                        mpClickSubpageEntryPoint(
                          experienceId,
                          "Audience Table Tooltip",
                          getTabName(config.metrics[0]),
                          config?.impactMetric ?? config.metrics[0]
                        );
                      } else {
                        onAddKeyMetricClick();
                      }
                    }}
                    sx={{
                      textDecoration: "underline",
                      "&:hover": { cursor: "pointer" },
                    }}
                    igStyle={"semibold"}
                  >
                    {pageName !== "overview" && config.canBeKeyMetric
                      ? ""
                      : `Explore ${getMetricCategoryName(config.impactMetric ?? config.metrics[0])} Metric`}
                  </IgTypography>
                </Grid>
                {config.hasTimeseries ||
                config.hasProbability ||
                config.hasValueCI ? (
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <IgTypography
                      igStyle={"semibold"}
                      color={"dark"}
                      sx={{
                        textDecoration: "underline",
                        "&:hover": { cursor: "pointer" },
                      }}
                      onClick={() => {
                        mpAnalyticsMetricDetailClick(
                          experienceId,
                          "Audience Table Tooltip",
                          config?.impactMetric ?? config.metrics[0]
                        );
                        void navigate(location.pathname + location.search, {
                          state: {
                            targetId: "metric-details-wrapper",
                            targetMetricId: config.metrics[0],
                          },
                          replace: true,
                        });
                      }}
                    >
                      Metric Details
                    </IgTypography>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
          )
        }
      >
        <Grid item>
          {showUplift && upliftValue ? (
            <UpliftChip
              upliftValue={upliftValue}
              isInverted={config.isInverted}
            />
          ) : null}
          <IgTypography
            sx={{
              fontWeight: 400,
            }}
          >
            {graphValueFormatter({
              isAxisTick: false,
              value: rawValue,
              configDataTypes: config.dataTypes,
              decimalAmountShown: shouldShowThreeDecimals ? 3 : undefined,
            })}
          </IgTypography>
        </Grid>
      </IgAnalyticsTooltip>
    </Grid>
  );
};
export const GroupCellRenderer = (
  params: ICellRendererParams & {
    tabValue: number;
    variations: VariationFe[];
    selectedRowId?: string | null;
    onRowSelected?: (id: string) => void;
    disabled?: boolean;
    disabledReason?: string;
    isRolloutRender?: boolean;
  }
) => {
  const {
    variations,
    tabValue,
    selectedRowId,
    onRowSelected,
    disabled = false,
    disabledReason = "",
    isRolloutRender,
  } = params;

  const handleRadioClick = (e) => {
    e.stopPropagation(); // Prevent row selection event
    if (onRowSelected && !disabled) {
      onRowSelected(params.data.variation_id);
    }
  };

  return (
    <Grid
      container
      direction={"row"}
      wrap={"nowrap"}
      alignItems={"center"}
      justifyContent={"space-between"}
      marginLeft={tabValue === 0 ? (isRolloutRender ? "5px" : "15px") : "35px"}
    >
      {isRolloutRender && (
        <Grid item>
          <IgAnalyticsTooltip title={disabledReason}>
            <IgRadio
              size={"small"}
              disabled={disabled}
              checked={params.data.variation_id === selectedRowId}
              onClick={handleRadioClick}
            />
          </IgAnalyticsTooltip>
        </Grid>
      )}
      <Grid item container width={"100%"} maxWidth={"150px"} marginLeft="10px">
        <IgAnalyticsTooltip
          title={variations.find((v) => v.id === params.value)?.name || ""}
        >
          <IgTypography
            sx={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              overflowWrap: "break-word",
            }}
          >
            {variations.find((v) => v.id === params.value)?.name || ""}
          </IgTypography>
        </IgAnalyticsTooltip>
      </Grid>
    </Grid>
  );
};

export const TableGenericCellRenderer = (params: ICellRendererParams) => {
  return (
    <Grid item container sx={{ justifyContent: "flex-end" }}>
      <IgTypography
        sx={{
          fontWeight: 400,
          textAlign: "flex-end",
        }}
      >
        {params.valueFormatted}
      </IgTypography>
    </Grid>
  );
};
